import { SourceTypesQueryResponse, useSourceTypesQuery } from "api/company";
import { AxiosError } from "axios";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { ApiError } from "../api/shared";

export enum SourceTypeStatus {
  EMPTY,
  LOADING,
  READY,
  ERROR,
}

const defaultState: SourceTypesContextType = {
  status: SourceTypeStatus.EMPTY,
};

export type SourceTypesContextType = {
  status: SourceTypeStatus;
  sourceTypes?: SourceTypesQueryResponse;
  error?: AxiosError<ApiError>;
};

const SourceTypesContext = createContext({} as SourceTypesContextType);

interface SourceTypesProviderProps {
  companyId: string;
  initialState?: SourceTypesContextType;
}

export function SourceTypesProvider({
  children,
  companyId,
  initialState = defaultState,
}: React.PropsWithChildren<SourceTypesProviderProps>) {
  const [{ data, loading, error }, getSourceTypes] = useSourceTypesQuery();
  const [status, setStatus] = useState<SourceTypeStatus>(initialState.status);

  useEffect(() => {
    async function fetchSourceTypes() {
      setStatus(SourceTypeStatus.LOADING);
      try {
        await getSourceTypes({ companyId });
        setStatus(SourceTypeStatus.READY);
      } catch (e) {
        setStatus(SourceTypeStatus.ERROR);
      }
    }

    if (companyId && status === SourceTypeStatus.EMPTY) {
      fetchSourceTypes();
    }
  }, [companyId, status, error, loading, getSourceTypes]);

  const sortedData = useMemo(() => {
    return data ? [...data].sort((a, b) => a.priority - b.priority) : undefined;
  }, [data]);

  const contextValue: SourceTypesContextType = {
    sourceTypes: sortedData || initialState.sourceTypes,
    status,
    error: error ?? undefined,
  };

  return (
    <SourceTypesContext.Provider value={contextValue}>
      {children}
    </SourceTypesContext.Provider>
  );
}

export const useSourceTypes = () => useContext(SourceTypesContext);
